import { NgModule } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { RouterModule, Routes } from '@angular/router';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import {
    AbsentComponent,
    AccountActionsComponent,
    AccountComponent,
    AnalysisAllListComponent,
    AnalysisComponent,
    AnalysisListSubjectComponent,
    AnalysisListSubjectHomeComponent,
    AnalyticService,
    AnalyticsListClassComponent,
    AnalyticsRedirectComponent,
    BoxEditComponent,
    CardsComponent,
    ClassAccountComponent,
    ClassComponent,
    CommonTableComponent,
    CreateNewLessonComponent,
    DashboardComponent,
    DeleteCardComponent,
    EditLessonBoxComponent,
    EditLessonComponent,
    ExerciseCalcComponent,
    ExerciseMagicBoxComponent,
    ExerciseTaskComponent,
    ExExerciseModalComponent,
    ManagePrivilegeComponent,
    PrepareAllListComponent,
    PrepareClassListComponent,
    PrepareComponent,
    PreparePrivilegesComponent,
    PrepareSubjectListComponent,
    PrepareSubjectListHomeComponent,
    PrintComponent,
    PrintMagicBoxComponent,
    PupilComponent,
    QuestionLogicComponent,
    QuestionTaskComponent,
    ResultExercisesComponent,
    ResultModalComponent,
    SelectClassComponent,
    StudentInfoComponent,
    StudentsListComponent,
    SummaryDataComponent,
    SummaryModalComponent,
    TasksComponent,
    TeachAllListComponent,
    TeachClassListComponent,
    TeachComponent,
    TeachSubjectListComponent,
    TeachSubjectListHomeComponent,
    TimeCorrectionModalComponent,
    HabirintTableComponent,
    IndividualDataComponent
} from './components';
import { IndividualDataModule } from '@teacher/components/analysis/analysis-list-subject-home/individual-data/individual-data.module';
import { TeacherService } from '@app/teacher/services/teacher.service';
import { LessonService } from '@app/teacher/services/lesson.service';
import { SchoolService } from '@app/teacher/services/school.service';
import { AuthInterceptor } from '@app/_interceptors/auth.interceptor';
import { AuthGuard } from '@app/_guards/auth.guard';
import { SharedModule } from '@app/shared/shared.module';
import { CoreModule } from '@app/core/core.module';
import { NgxBootstrapModule } from '@app/ngx-bootstrap/ngx-bootstrap.module';
import { GoogleChartsModule } from 'angular-google-charts';
import { SvgIconModule } from '@app/core/components/svg-icon/svg-icon.module';
import { BoxEditModule } from '@app/teacher/components/prepare/box-edit/box-edit.module';
import { PreviewImgModule } from '@app/shared/components/preview-img/preview-img.module';
import {
    MatDialogModule,
    MatSnackBarModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatProgressSpinnerModule
} from '@angular/material';
import { CopyFormComponent } from '@app/teacher/components/prepare/copy-form/copy-form.component';
import { DeleteFormComponent } from '@app/teacher/components/prepare/delete-form/delete-form.component';
import { EditLessonModalComponent } from '@app/teacher/components/prepare/edit-lesson-modal/edit-lesson-modal.component';
import { LessonsListModule } from '@app/teacher/components/lessons-list/lessons-list.module';
import { HomeComponent } from '@app/core/components/home/home.component';

const teacherRoutes: Routes = [
    {
        path: '',
        component: DashboardComponent,
        canActivate: [AuthGuard],
        children: [
            {
                path: 'accounts',
                component: AccountComponent,
                canActivate: [AuthGuard],
                children: [
                    { path: '', redirectTo: 'select', pathMatch: 'full' },
                    { path: 'select', component: SelectClassComponent },
                    { path: ':class_id', component: ClassAccountComponent }
                ]
            },
            {
                path: 'analysis',
                component: AnalysisComponent,
                canActivate: [AuthGuard],
                children: [
                    { path: '', component: AnalysisAllListComponent },
                    {
                        path: ':class_id',
                        component: AnalyticsListClassComponent
                    },
                    {
                        path: ':class_id/:subject_id',
                        component: AnalysisListSubjectHomeComponent,
                        children: [
                            { path: '', redirectTo: 'list', pathMatch: 'full' },
                            {
                                path: 'list',
                                component: AnalysisListSubjectComponent
                            },
                            {
                                path: 'analytics/:lesson_id',
                                component: AnalyticsRedirectComponent,
                                children: [
                                    { path: '', redirectTo: 'common', pathMatch: 'full' },
                                    { path: 'commontable', component: CommonTableComponent },
                                    { path: 'summary', component: SummaryDataComponent },
                                    { path: 'individual', component: IndividualDataComponent },
                                    { path: 'individual/:pupil_id', component: IndividualDataComponent }
                                ]
                            }
                        ]
                    }
                ]
            },
            {
                path: 'prepare',
                component: PrepareComponent,
                canActivate: [AuthGuard],
                children: [
                    { path: '', component: PrepareAllListComponent },
                    { path: ':lesson_id/edit', component: EditLessonComponent },
                    { path: ':lesson_id/edit/box/:box_id', component: BoxEditComponent },
                    {
                        path: 'new',
                        component: CreateNewLessonComponent,
                        children: [{ path: ':lesson_id', component: CreateNewLessonComponent }]
                    },
                    { path: ':class_id', component: PrepareClassListComponent },
                    {
                        path: ':class_id/:subject_id',
                        component: PrepareSubjectListHomeComponent,
                        children: [
                            { path: '', redirectTo: 'list', pathMatch: 'full' },
                            {
                                path: 'list',
                                component: PrepareSubjectListComponent
                            }
                        ]
                    }
                ]
            },
            {
                path: 'teach',
                component: TeachComponent,
                canActivate: [AuthGuard],
                children: [
                    { path: '', component: TeachAllListComponent },
                    { path: ':class_id', component: TeachClassListComponent },
                    {
                        path: ':class_id/:subject_id',
                        component: TeachSubjectListHomeComponent,
                        children: [
                            { path: '', redirectTo: 'list', pathMatch: 'full' },
                            {
                                path: 'list',
                                component: TeachSubjectListComponent
                            },
                            { path: 'progress/:lesson_id', component: TasksComponent }
                        ]
                    }
                ]
            },
            { path: 'print/:lesson_id', component: PrintComponent, canActivate: [AuthGuard] },
            { path: 'print/box/:box_id', component: PrintComponent, canActivate: [AuthGuard] }
        ]
    }
];

@NgModule({
    imports: [
        SvgIconModule,
        LessonsListModule,
        CommonModule,
        HttpClientModule,
        ReactiveFormsModule,
        FormsModule,
        CoreModule,
        SharedModule,
        NgxBootstrapModule,
        GoogleChartsModule,
        PreviewImgModule,
        BoxEditModule,
        IndividualDataModule,
        RouterModule.forChild(teacherRoutes),
        // material
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatSnackBarModule,
        MatCheckboxModule,
        MatProgressSpinnerModule
    ],
    exports: [],
    declarations: [
        HomeComponent,
        DashboardComponent,
        PrepareComponent,
        AnalysisComponent,
        AccountComponent,
        TeachComponent,
        PrepareAllListComponent,
        DeleteFormComponent,
        CopyFormComponent,
        ExExerciseModalComponent,
        EditLessonModalComponent,
        CreateNewLessonComponent,
        AnalysisAllListComponent,
        AnalysisListSubjectHomeComponent,
        AnalysisListSubjectComponent,
        AnalyticsRedirectComponent,
        CommonTableComponent,
        SummaryDataComponent,
        AnalyticsListClassComponent,
        TeachAllListComponent,
        TeachClassListComponent,
        TeachSubjectListHomeComponent,
        TeachSubjectListComponent,
        TasksComponent,
        CardsComponent,
        ExerciseTaskComponent,
        ExerciseMagicBoxComponent,
        ExerciseCalcComponent,
        QuestionTaskComponent,
        QuestionLogicComponent,
        TimeCorrectionModalComponent,
        PrepareClassListComponent,
        PrepareSubjectListHomeComponent,
        PrepareSubjectListComponent,
        PrintComponent,
        PrintMagicBoxComponent,
        PreparePrivilegesComponent,
        ClassComponent,
        PupilComponent,
        ManagePrivilegeComponent,
        AbsentComponent,
        EditLessonBoxComponent,
        EditLessonComponent,
        SummaryModalComponent,
        ClassAccountComponent,
        StudentsListComponent,
        StudentInfoComponent,
        SelectClassComponent,
        AccountActionsComponent,
        ResultModalComponent,
        ResultExercisesComponent,
        DeleteCardComponent,
        HabirintTableComponent
    ],
    entryComponents: [CopyFormComponent, DeleteFormComponent, EditLessonModalComponent],
    providers: [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true
        },
        DatePipe,
        TeacherService,
        LessonService,
        SchoolService,
        AnalyticService
    ]
})
export class TeacherModule {}
